import { useEffect, useState, useCallback, FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import DeleteConfirmModal from '@/pages/admin/components/menu/DeleteConfirmModal';

interface Product {
  id: string;
  name_de: string;
  category_id: string;
}

interface Extra {
  id: string;
  product_id: string;
  name_de: string;
  name_en: string;
  price: number;
  sort_order: number;
}

interface ExtraFormState {
  product_id: string;
  name_de: string;
  name_en: string;
  price: number;
  sort_order: number;
}

const emptyForm = (productId: string): ExtraFormState => ({
  product_id: productId,
  name_de: '',
  name_en: '',
  price: 0,
  sort_order: 0,
});

const ExtrasTab = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [extras, setExtras] = useState<Extra[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [editingExtra, setEditingExtra] = useState<Extra | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ExtraFormState>(emptyForm(''));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: prods }, { data: exts }] = await Promise.all([
      supabase.from('products').select('id, name_de, category_id').eq('active', true).order('name_de'),
      supabase.from('extras').select('*').order('sort_order'),
    ]);
    if (prods) {
      setProducts(prods);
      if (!selectedProduct && prods.length > 0) setSelectedProduct(prods[0].id);
    }
    if (exts) setExtras(exts);
    setLoading(false);
  }, [selectedProduct]);

  useEffect(() => { fetchAll(); }, []);

  const filteredExtras = extras.filter((e) => e.product_id === selectedProduct);
  const selectedProductName = products.find((p) => p.id === selectedProduct)?.name_de ?? '';

  const openAdd = () => {
    setEditingExtra(null);
    setForm(emptyForm(selectedProduct));
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (extra: Extra) => {
    setEditingExtra(extra);
    setForm({
      product_id: extra.product_id,
      name_de: extra.name_de,
      name_en: extra.name_en,
      price: extra.price,
      sort_order: extra.sort_order,
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name_de.trim()) { setFormError('Ekstra adı (Almanca) zorunludur.'); return; }
    if (!form.product_id) { setFormError('Ürün seçiniz.'); return; }

    setSaving(true);
    setFormError(null);

    const payload = {
      product_id: form.product_id,
      name_de: form.name_de.trim(),
      name_en: form.name_en.trim() || form.name_de.trim(),
      price: Number(form.price),
      sort_order: Number(form.sort_order),
    };

    let err;
    if (editingExtra) {
      ({ error: err } = await supabase.from('extras').update(payload).eq('id', editingExtra.id));
    } else {
      ({ error: err } = await supabase.from('extras').insert(payload));
    }

    setSaving(false);
    if (err) { setFormError('Kayıt hatası: ' + err.message); return; }
    setShowForm(false);
    fetchAll();
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    await supabase.from('extras').update({ price: -1 }).eq('id', deleteModal.id);
    // Actually delete
    await supabase.from('extras').delete().eq('id', deleteModal.id);
    setDeleting(false);
    setDeleteModal(null);
    fetchAll();
  };

  const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all';
  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1.5';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Ekstralar Yönetimi</h2>
          <p className="text-gray-500 text-sm">Ürünlere eklenebilecek ekstraları yönetin</p>
        </div>
        {selectedProduct && (
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            Ekstra Ekle
          </button>
        )}
      </div>

      {/* Product selector */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Ürün Seç</label>
        {loading ? (
          <div className="flex items-center justify-center h-10">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedProduct(p.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border
                  ${selectedProduct === p.id
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-500'
                  }`}
              >
                {p.name_de}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Extras list */}
      {selectedProduct && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">
              {selectedProductName} — Ekstralar
              <span className="ml-2 text-xs text-gray-400 font-normal">({filteredExtras.length} adet)</span>
            </p>
          </div>
          {filteredExtras.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-gray-300">
                <i className="ri-add-circle-line text-3xl"></i>
              </div>
              <p className="text-gray-400 text-sm">Bu ürün için ekstra yok</p>
              <button
                onClick={openAdd}
                className="mt-3 text-orange-500 text-sm font-medium hover:underline cursor-pointer"
              >
                İlk ekstrayi ekle
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ad (Almanca)</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Ad (İngilizce)</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fiyat</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredExtras.map((extra) => (
                  <tr key={extra.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 text-sm font-medium text-gray-900">{extra.name_de}</td>
                    <td className="px-5 py-3 text-sm text-gray-500 hidden sm:table-cell">{extra.name_en}</td>
                    <td className="px-5 py-3">
                      <span className="text-sm font-bold text-gray-900">
                        {Number(extra.price) === 0 ? 'Ücretsiz' : `+CHF ${Number(extra.price).toFixed(2)}`}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(extra)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-all cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteModal({ id: extra.id, name: extra.name_de })}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Inline form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">
                {editingExtra ? 'Ekstra Düzenle' : 'Yeni Ekstra'}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
              <div>
                <label className={labelCls}>Ürün</label>
                <select
                  value={form.product_id}
                  onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value }))}
                  className={inputCls}
                >
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name_de}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Ad (Almanca) *</label>
                  <input
                    type="text"
                    value={form.name_de}
                    onChange={(e) => setForm((f) => ({ ...f, name_de: e.target.value }))}
                    placeholder="z.B. Extra Käse"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Ad (İngilizce)</label>
                  <input
                    type="text"
                    value={form.name_en}
                    onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
                    placeholder="e.g. Extra Cheese"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Fiyat (CHF)</label>
                  <input
                    type="number"
                    step="0.50"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm((f) => ({ ...f, price: Number(e.target.value) }))}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Sıralama</label>
                  <input
                    type="number"
                    min="0"
                    value={form.sort_order}
                    onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
                    className={inputCls}
                  />
                </div>
              </div>
              {formError && (
                <div className="flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg bg-red-50 text-red-600">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line"></i>
                  </div>
                  {formError}
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  {editingExtra ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal && (
        <DeleteConfirmModal
          title="Ekstra Sil"
          description={`"${deleteModal.name}" silinecek. Bu işlem geri alınamaz.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteModal(null)}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default ExtrasTab;
